import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { Moon, Send, CheckCircle2 } from 'lucide-react';
import { submitAfterHoursEnquiry } from '@/lib/api';

const EMPTY = { name: '', phone: '', email: '', vehicle: '', preferred_time: '', notes: '' };

export default function AfterHoursEnquiryDialog({ open, onOpenChange }) {
  const [form, setForm] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const setField = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  const canSubmit =
    form.name.trim() &&
    form.phone.trim() &&
    /@/.test(form.email) &&
    form.vehicle.trim() &&
    form.preferred_time.trim();

  const close = () => {
    onOpenChange(false);
    // Reset after a short delay so form state doesn't flash during the close animation
    setTimeout(() => {
      setForm(EMPTY);
      setSubmitted(false);
    }, 300);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit || submitting) return;
    setSubmitting(true);
    try {
      await submitAfterHoursEnquiry({
        name: form.name.trim(),
        phone: form.phone.trim(),
        email: form.email.trim(),
        vehicle: form.vehicle.trim(),
        preferred_time: form.preferred_time.trim(),
        notes: form.notes.trim() || null,
      });
      setSubmitted(true);
      toast.success('Enquiry sent — William will be in touch');
    } catch (err) {
      const msg =
        err?.response?.data?.detail || err?.message || 'Could not send enquiry — please call William on 0278150446.';
      toast.error(typeof msg === 'string' ? msg : 'Could not send enquiry');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => (v ? onOpenChange(true) : close())}>
      <DialogContent
        className="sm:max-w-lg bg-zinc-900 border-zinc-800 max-h-[90vh] overflow-y-auto"
        data-testid="after-hours-dialog"
      >
        <DialogHeader>
          <div className="flex items-center gap-2 mb-1">
            <Moon className="w-5 h-5 text-green-500" />
            <DialogTitle className="font-['Barlow_Condensed'] text-2xl font-bold uppercase tracking-tight text-white">
              Request an After-Hours Slot
            </DialogTitle>
          </div>
          <DialogDescription className="text-zinc-400">
            Need a dyno session outside Mon–Sat 8am–5pm? Send through your details and William will
            come back to you to confirm a time.
          </DialogDescription>
        </DialogHeader>

        {submitted ? (
          <div className="py-6 text-center space-y-4" data-testid="after-hours-success">
            <CheckCircle2 className="w-14 h-14 mx-auto text-green-500" />
            <p className="text-white font-['Barlow_Condensed'] text-xl uppercase tracking-wide">
              Enquiry received
            </p>
            <p className="text-zinc-400 text-sm max-w-sm mx-auto">
              Thanks {form.name.split(' ')[0] || 'legend'} — William will be in touch shortly to
              lock in your after-hours time slot.
            </p>
            <Button
              onClick={close}
              className="bg-green-600 hover:bg-green-500 text-white"
              data-testid="after-hours-close-btn"
            >
              Done
            </Button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-3 mt-2" data-testid="after-hours-form">
            <div>
              <Label htmlFor="ah-name" className="text-zinc-300 text-xs uppercase tracking-wider">
                Your name *
              </Label>
              <Input
                id="ah-name"
                value={form.name}
                onChange={setField('name')}
                className="bg-zinc-950 border-zinc-800 text-white mt-1"
                data-testid="after-hours-name"
                required
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label htmlFor="ah-phone" className="text-zinc-300 text-xs uppercase tracking-wider">
                  Phone *
                </Label>
                <Input
                  id="ah-phone"
                  type="tel"
                  value={form.phone}
                  onChange={setField('phone')}
                  className="bg-zinc-950 border-zinc-800 text-white mt-1"
                  data-testid="after-hours-phone"
                  required
                />
              </div>
              <div>
                <Label htmlFor="ah-email" className="text-zinc-300 text-xs uppercase tracking-wider">
                  Email *
                </Label>
                <Input
                  id="ah-email"
                  type="email"
                  value={form.email}
                  onChange={setField('email')}
                  className="bg-zinc-950 border-zinc-800 text-white mt-1"
                  data-testid="after-hours-email"
                  required
                />
              </div>
            </div>
            <div>
              <Label htmlFor="ah-vehicle" className="text-zinc-300 text-xs uppercase tracking-wider">
                Vehicle (year/make/model) *
              </Label>
              <Input
                id="ah-vehicle"
                value={form.vehicle}
                onChange={setField('vehicle')}
                placeholder="e.g. 2008 Nissan Skyline V36"
                className="bg-zinc-950 border-zinc-800 text-white mt-1"
                data-testid="after-hours-vehicle"
                required
              />
            </div>
            <div>
              <Label htmlFor="ah-time" className="text-zinc-300 text-xs uppercase tracking-wider">
                Preferred time *
              </Label>
              <Input
                id="ah-time"
                value={form.preferred_time}
                onChange={setField('preferred_time')}
                placeholder="e.g. Weeknight after 6pm, or Sunday morning"
                className="bg-zinc-950 border-zinc-800 text-white mt-1"
                data-testid="after-hours-time"
                required
              />
            </div>
            <div>
              <Label htmlFor="ah-notes" className="text-zinc-300 text-xs uppercase tracking-wider">
                Anything else?
              </Label>
              <Textarea
                id="ah-notes"
                value={form.notes}
                onChange={setField('notes')}
                rows={3}
                placeholder="Tune type, mods, budget, etc. — optional"
                className="bg-zinc-950 border-zinc-800 text-white mt-1"
                data-testid="after-hours-notes"
              />
            </div>

            <DialogFooter className="pt-2">
              <Button
                type="button"
                variant="ghost"
                onClick={close}
                className="text-zinc-400 hover:text-white"
                data-testid="after-hours-cancel-btn"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={!canSubmit || submitting}
                className="bg-green-600 hover:bg-green-500 text-white"
                data-testid="after-hours-submit-btn"
              >
                <Send className="w-4 h-4 mr-2" />
                {submitting ? 'Sending…' : 'Send Enquiry'}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
